import { Children, useCallback, useEffect, useRef, useState } from 'react'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import classNames from 'classnames'
import styles from './SlidesCarousel.module.scss'
import { SlidesCarouselProps } from './SlidesCarousel.types'

// scrollLeft can land a fraction of a pixel short of either end, which should
// still count as being at that end.
const EDGE_TOLERANCE = 1

export const SlidesCarousel = ({
  children,
  tag = 'div',
  className,
  trackClassName,
  slideClassName,
  buttonClassName,
  activeIndex,
  scrollStepRatio = 0.8,
  chevronSize = 16,
  prevLabel = 'Scroll left',
  nextLabel = 'Scroll right',
}: SlidesCarouselProps) => {
  const trackRef = useRef<HTMLDivElement & HTMLUListElement>(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const slideCount = Children.count(children)

  const updateOverflow = useCallback(() => {
    const track = trackRef.current

    if (!track) {
      return
    }

    const maxScrollLeft = track.scrollWidth - track.clientWidth

    setCanScrollPrev(track.scrollLeft > EDGE_TOLERANCE)
    setCanScrollNext(track.scrollLeft < maxScrollLeft - EDGE_TOLERANCE)
  }, [])

  // The chevrons follow the scroll position and the sizes of the track and
  // its slides, which can change after mount as content inside them loads.
  useEffect(() => {
    const track = trackRef.current

    if (!track) {
      return undefined
    }

    updateOverflow()

    const resizeObserver = new ResizeObserver(updateOverflow)
    resizeObserver.observe(track)

    Array.from(track.children).forEach((slide) => {
      resizeObserver.observe(slide)
    })

    track.addEventListener('scroll', updateOverflow, { passive: true })

    return () => {
      resizeObserver.disconnect()
      track.removeEventListener('scroll', updateOverflow)
    }
  }, [updateOverflow, slideCount])

  // Jump straight to the active slide on mount; animate to it after that.
  const hasScrolledToActiveRef = useRef(false)

  useEffect(() => {
    const track = trackRef.current

    if (!track || activeIndex === undefined) {
      return
    }

    const slide = track.children[activeIndex] as HTMLElement | undefined

    if (!slide) {
      return
    }

    // offsetLeft is measured against the track, which is positioned for this.
    const slideStart = slide.offsetLeft
    const slideEnd = slideStart + slide.offsetWidth
    const viewStart = track.scrollLeft
    const viewEnd = viewStart + track.clientWidth
    const behavior = hasScrolledToActiveRef.current ? 'smooth' : 'auto'
    hasScrolledToActiveRef.current = true

    if (slideStart < viewStart) {
      track.scrollTo({ left: slideStart, behavior })
    } else if (slideEnd > viewEnd) {
      track.scrollTo({ left: slideEnd - track.clientWidth, behavior })
    }
  }, [activeIndex])

  const scrollByStep = (direction: -1 | 1) => {
    const track = trackRef.current

    if (!track) {
      return
    }

    track.scrollBy({
      left: direction * track.clientWidth * scrollStepRatio,
      behavior: 'smooth',
    })
  }

  const slideClass = classNames(styles.slide, slideClassName)
  const trackClass = classNames(styles.track, trackClassName)
  const slides = Children.map(children, (child) => {
    if (child === null || child === undefined || typeof child === 'boolean') {
      return null
    }

    if (tag === 'ul') {
      return <li className={slideClass}>{child}</li>
    }

    return <div className={slideClass}>{child}</div>
  })

  return (
    <div className={classNames(styles.root, className)}>
      {tag === 'ul' ? (
        <ul ref={trackRef} className={trackClass}>
          {slides}
        </ul>
      ) : (
        <div ref={trackRef} className={trackClass}>
          {slides}
        </div>
      )}

      {canScrollPrev && (
        <button
          type='button'
          aria-label={prevLabel}
          onClick={() => {
            scrollByStep(-1)
          }}
          className={classNames(styles.button, styles.prev, buttonClassName)}>
          <IconChevronLeft size={chevronSize} stroke={2.5} aria-hidden='true' />
        </button>
      )}

      {canScrollNext && (
        <button
          type='button'
          aria-label={nextLabel}
          onClick={() => {
            scrollByStep(1)
          }}
          className={classNames(styles.button, styles.next, buttonClassName)}>
          <IconChevronRight
            size={chevronSize}
            stroke={2.5}
            aria-hidden='true'
          />
        </button>
      )}
    </div>
  )
}
